/**
 * AutocueView — public, no-auth teleprompter for an autocue session slug.
 *
 * Mobile-first. Loads a script by slug, renders it scrolling upwards at a
 * configurable speed, with controls for font size, scroll speed, play/pause
 * and mirror (for hardware autocue rigs). Designed so an estate agent can:
 *   - Scan the QR on their proposal builder
 *   - Lay their phone flat below the camera
 *   - Tap Play and read at a natural pace
 */

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Play, Pause, RotateCw, ChevronUp, ChevronDown, Type, Gauge,
  FlipHorizontal, ArrowLeft, Loader2,
} from "lucide-react";
import "./AutocueView.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AutocueView({ slug }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  // Teleprompter controls
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(35);     // pixels per second
  const [fontSize, setFontSize] = useState(36); // px
  const [mirrored, setMirrored] = useState(false);

  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/autocue/sessions/${slug}`);
        if (!cancelled) setSession(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Session not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Smooth pixel-accurate auto-scroll while "playing"
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = 0;
      return;
    }
    const step = (ts) => {
      const el = scrollRef.current;
      if (!el) return;
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      el.scrollTop += speed * dt;
      // Stop when we reach the bottom
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
        setPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed]);

  const restart = () => {
    setPlaying(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const paragraphs = useMemo(() => {
    if (!session?.text) return [];
    return session.text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  }, [session]);

  if (loading) {
    return (
      <div className="autocue-empty">
        <Loader2 size={28} className="autocue-spin" />
        <p>Loading session…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="autocue-empty">
        <p className="autocue-error">{error}</p>
        <button className="autocue-link-btn" onClick={() => (window.location.href = "/")}>
          Go to home
        </button>
      </div>
    );
  }

  return (
    <div className="autocue-root" data-testid="autocue-view">
      <header className="autocue-bar autocue-bar-top">
        <button
          className="autocue-icon-btn"
          onClick={() => (window.location.href = "/")}
          title="Back"
          data-testid="autocue-back"
        >
          <ArrowLeft size={18} />
        </button>
        <span className="autocue-title">{session?.title || "Autocue"}</span>
        <button
          className="autocue-icon-btn"
          onClick={() => setMirrored((v) => !v)}
          title="Flip horizontally (for teleprompter rigs)"
          data-testid="autocue-mirror"
          aria-pressed={mirrored}
        >
          <FlipHorizontal size={18} />
        </button>
      </header>

      <div
        ref={scrollRef}
        className={`autocue-scroll ${mirrored ? "autocue-mirrored" : ""}`}
        style={{ fontSize: `${fontSize}px` }}
      >
        {/* Top spacer pushes the first paragraph into view from below */}
        <div className="autocue-spacer-top" />
        {paragraphs.map((p, i) => (
          <p key={i} className="autocue-para">{p}</p>
        ))}
        <div className="autocue-spacer-bottom" />
      </div>

      {/* Eye-line marker — gives the agent a fixed point to read to */}
      <div className="autocue-eyeline" aria-hidden="true" />

      <footer className="autocue-bar autocue-bar-bottom">
        <div className="autocue-ctrl-row">
          <button
            className={`autocue-play-btn ${playing ? "is-playing" : ""}`}
            onClick={() => setPlaying((v) => !v)}
            data-testid="autocue-play"
          >
            {playing ? <Pause size={20} /> : <Play size={20} />}
            {playing ? "Pause" : "Play"}
          </button>
          <button className="autocue-icon-btn" onClick={restart} title="Restart" data-testid="autocue-restart">
            <RotateCw size={18} />
          </button>
        </div>

        <div className="autocue-ctrl-row autocue-ctrl-row-meta">
          <div className="autocue-ctrl">
            <Type size={14} />
            <button
              className="autocue-step-btn"
              onClick={() => setFontSize((s) => Math.max(20, s - 4))}
              aria-label="Smaller text"
            >
              <ChevronDown size={14} />
            </button>
            <span className="autocue-ctrl-val">{fontSize}</span>
            <button
              className="autocue-step-btn"
              onClick={() => setFontSize((s) => Math.min(96, s + 4))}
              aria-label="Larger text"
            >
              <ChevronUp size={14} />
            </button>
          </div>
          <div className="autocue-ctrl">
            <Gauge size={14} />
            <button
              className="autocue-step-btn"
              onClick={() => setSpeed((s) => Math.max(10, s - 5))}
              aria-label="Slower"
            >
              <ChevronDown size={14} />
            </button>
            <span className="autocue-ctrl-val">{speed}</span>
            <button
              className="autocue-step-btn"
              onClick={() => setSpeed((s) => Math.min(150, s + 5))}
              aria-label="Faster"
            >
              <ChevronUp size={14} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
